import type { FAIconName } from '#shared/types/generated-icons'

interface UseNarrationReturn {
  speak: () => void
  stop: () => void
  onClick: () => void
  isPlaying: Ref<boolean>
  isSupported: Ref<boolean>
  buttonIcon: Ref<FAIconName>
  ariaLabel: Ref<string>
}

function hasNoNarrationAncestor(el: Element | null): boolean {
  while (el) {
    if (el instanceof HTMLElement && el.dataset && 'noNarration' in el.dataset) {
      return true
    }
    el = el.parentElement
  }
  return false
}

export function useNarration(
  elementRef: MaybeRefOrGetter<HTMLElement | null>
): UseNarrationReturn {
  if (import.meta.server) {
    return {
      speak: () => {},
      stop: () => {},
      onClick: () => {},
      isPlaying: ref(false),
      isSupported: ref(false),
      buttonIcon: ref('fa-play'),
      ariaLabel: ref('Narration not supported on server')
    }
  }

  const localeStore = useLocaleStore()

  const {
    speak: innerSpeak,
    stop,
    isPlaying,
    isSupported,
    utterance
  } = useSpeechSynthesis('', {
    lang: () => (localeStore.localeIsDutch ? 'nl-NL' : 'en-GB')
  })

  const getCleanText = (): string | undefined => {
    const element = toValue(elementRef)
    if (!element) {
      return undefined
    }

    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)

    let text = ''
    let nextNode = walker.nextNode()
    while (nextNode) {
      const parent = nextNode.parentElement
      if (hasNoNarrationAncestor(parent)) {
        nextNode = walker.nextNode()
        continue
      }

      const nodeText = nextNode.textContent?.trim() || ''

      if (!nodeText) {
        nextNode = walker.nextNode()
        continue
      }

      const isQuote = parent?.nodeName === 'BLOCKQUOTE' || parent?.nodeName === 'Q'
      if (isQuote) {
        text += localeStore.localeIsDutch ? 'Citaat: ' : 'Quote: '
      }

      text += nodeText

      if (parent && ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(parent?.nodeName)) {
        const lastCharacter = text.at(-1)
        if (lastCharacter && ['.', ',', '!', '?'].includes(lastCharacter)) {
          text += ' '
        } else {
          text += '. '
        }
      } else if (parent && ['P', 'LI', 'Q'].includes(parent?.nodeName)) {
        const lastCharacter = text.at(-1)

        if (isQuote) {
          text += localeStore.localeIsDutch ? ' Einde citaat. ' : ' End of quote. '
        } else if (lastCharacter && ['.', ',', '!', '?'].includes(lastCharacter)) {
          text += ' '
        } else {
          text += '. '
        }
      }

      nextNode = walker.nextNode()
    }

    return text
  }

  const speak = () => {
    const text = getCleanText()
    if (!text) {
      return
    }

    utterance.value.text = text
    innerSpeak()
  }

  const onClick = () => {
    if (isPlaying.value) {
      stop()
    } else {
      speak()
    }
  }

  const buttonIcon = computed<FAIconName>(() => {
    if (isPlaying.value) {
      return 'fa-stop'
    } else {
      return 'fa-play'
    }
  })

  const ariaLabel = computed(() => {
    if (isPlaying.value) {
      return localeStore.localeIsDutch ? 'Stop met voorlezen' : 'Stop narration'
    } else {
      return localeStore.localeIsDutch ? 'Tekst voorlezen' : 'Start narration'
    }
  })

  onUnmounted(() => {
    if (!isSupported.value) {
      return
    }

    stop()
  })

  return {
    speak,
    stop,
    onClick,
    isPlaying,
    isSupported,
    buttonIcon,
    ariaLabel
  }
}
